import { useEffect, useState } from 'react';
import {
  Callout,
  Empty,
  Field,
  FilterBar,
  Loading,
  Panel,
  Select,
  TableActions,
  TableScroll,
  formatDateTime,
  isApiClientError,
  useLoad,
} from '@affdice/portal-kit';

import type { PlatformApi } from '../../client.ts';
import { t } from '../../messages.ts';
import { Failure } from '../shared.tsx';
import {
  ACCESS_LOG_KINDS,
  accessLogExport,
  accessLogQuery,
  accessLogWhat,
  kindLabel,
  platformUserName,
  type AccessLogFilter,
  type AccessLogRow,
} from './access-log-model.ts';

/**
 * Integrity → Access log — every cross-operator read and act platform staff
 * have made (Rule Book 3.9c, 3.9d, 2.7, 3.13), newest first, filterable by
 * operator and kind, a page at a time. The export writes the rows loaded so
 * far, exactly as the table shows them.
 */
export function AccessLog({ api }: { api: PlatformApi }) {
  const [operatorId, setOperatorId] = useState('');
  const [kind, setKind] = useState<AccessLogFilter>('all');
  const [version, setVersion] = useState(0);
  const [older, setOlder] = useState<AccessLogRow[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);

  const operators = useLoad(() => api.operators(), []);
  const first = useLoad(() => api.accessLog(accessLogQuery({ operatorId, kind })), [operatorId, kind, version]);

  // A new first page (filters changed, or a retry) starts the list again.
  useEffect(() => {
    setOlder([]);
    setMoreError(null);
    setNext(first.state === 'ready' ? first.value.next_before : null);
  }, [first]);

  const rows: AccessLogRow[] = first.state === 'ready' ? [...first.value.items, ...older] : [];
  const filtered = operatorId !== '' || kind !== 'all';

  const loadMore = () => {
    if (!next) return;
    setLoadingMore(true);
    setMoreError(null);
    api
      .accessLog(accessLogQuery({ operatorId, kind, before: next }))
      .then((page) => {
        setOlder((o) => [...o, ...page.items]);
        setNext(page.next_before);
      })
      .catch(setMoreError)
      .finally(() => setLoadingMore(false));
  };

  const operatorOptions = operators.state === 'ready'
    ? [...operators.value.items].sort((a, b) => a.display_name.localeCompare(b.display_name))
    : [];
  const exported = accessLogExport(rows);

  return (
    <>
      <div className="page-head"><h1>{t('nav.accessLog')}</h1></div>
      <p className="faint">{t('integrity.accessLog.intro')}</p>

      <FilterBar>
        <Field label={t('common.operator')}>
          <Select
            value={operatorId}
            onChange={setOperatorId}
            options={[
              { value: '', label: t('integrity.accessLog.everyOperator') },
              ...operatorOptions.map((o) => ({ value: o.id, label: o.display_name })),
            ]}
          />
        </Field>
        <Field label={t('integrity.accessLog.col.kind')}>
          <Select
            value={kind}
            onChange={(v) => setKind(v as AccessLogFilter)}
            searchable={false}
            options={[
              { value: 'all', label: t('integrity.accessLog.everyKind') },
              ...ACCESS_LOG_KINDS.map((k) => ({ value: k, label: kindLabel(k) })),
            ]}
          />
        </Field>
      </FilterBar>

      {first.state === 'loading' ? <Loading rows={4} /> : null}
      {first.state === 'error' ? (
        isApiClientError(first.error) && first.error.status === 403 ? (
          <Callout tone="caution" fold={false}>{t('integrity.accessLog.forbidden')}</Callout>
        ) : (
          <>
            <Failure error={first.error} />
            <div className="form-actions">
              <button type="button" className="copy" onClick={() => setVersion((v) => v + 1)}>
                {t('integrity.accessLog.retry')}
              </button>
            </div>
          </>
        )
      ) : null}

      {first.state === 'ready' ? (
        <Panel
          title={t('integrity.accessLog.panel')}
          aside={
            <div className="panel-aside">
              <span className="count count-badge">{rows.length}{next ? '+' : ''}</span>
              <TableActions
                title={t('nav.accessLog')}
                filename="access-log"
                headers={exported.headers}
                keys={exported.keys}
                rows={exported.rows}
              />
            </div>
          }
        >
          {rows.length === 0 ? (
            <Empty>{filtered ? t('integrity.accessLog.emptyFiltered') : t('integrity.accessLog.empty')}</Empty>
          ) : (
            <>
              <TableScroll>
                <table>
                  <thead>
                    <tr>
                      <th scope="col">{t('integrity.accessLog.col.when')}</th>
                      <th scope="col">{t('integrity.accessLog.col.who')}</th>
                      <th scope="col">{t('common.operator')}</th>
                      <th scope="col">{t('integrity.accessLog.col.what')}</th>
                      <th scope="col">{t('common.reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={`${r.kind}-${r.id}`}>
                        <td className="date faint">{formatDateTime(r.at)}</td>
                        <td>
                          {platformUserName(r)}
                          {r.platform_user.email && r.platform_user.name ? (
                            <div className="faint">{r.platform_user.email}</div>
                          ) : null}
                        </td>
                        <td>{r.operator ?? <span className="mono faint">{r.operator_id ?? '—'}</span>}</td>
                        <td className="wrap">{accessLogWhat(r)}</td>
                        <td className="wrap">{r.reason ?? <span className="faint">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
              {moreError ? <Failure error={moreError} /> : null}
              <div className="form-actions">
                {next ? (
                  <button type="button" className="copy" disabled={loadingMore} onClick={loadMore}>
                    {loadingMore ? t('integrity.accessLog.loadingMore') : t('integrity.accessLog.more')}
                  </button>
                ) : (
                  <span className="faint">{t('integrity.accessLog.end')}</span>
                )}
              </div>
            </>
          )}
        </Panel>
      ) : null}
    </>
  );
}
